import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, Clock, Video, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1714636608872-048fc9231892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxjYWxtJTIwd2FybSUyMGJlaWdlJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODgwMTc4OTh8MA&ixlib=rb-4.1.0&q=85&auto=format&fit=crop&w=2000";

const LINES = ["Lass los,", "was dich", "innerlich festhält."];
const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <header id="hero" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex flex-col overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Warme, ruhige Wellenstruktur in Beigetönen"
          className="h-[124%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-cream/55" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-cream to-transparent" />
      </motion.div>

      <nav data-testid="hero-nav" className="relative z-10 flex items-center justify-between px-6 md:px-14 pt-7">
        <motion.img
          data-testid="nav-logo"
          src="/assets/logo.png"
          alt="einfach glücklich – Praxis für Hypnose und Meditation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="h-[43px] md:h-[53px] w-auto"
        />
        <motion.button
          data-testid="nav-booking-button"
          onClick={onBook}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="rounded-full border border-espresso/30 px-5 py-2 text-xs md:text-sm font-medium tracking-wide text-espresso transition-colors duration-300 hover:bg-espresso hover:text-cream"
        >
          Platz sichern
        </motion.button>
      </nav>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-1 flex-col justify-center px-6 md:px-14 py-20">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="mb-8 text-xs md:text-sm font-medium uppercase tracking-[0.32em] text-bronze"
        >
          Online-Hypnomeditation · live via Zoom
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif text-espresso tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl max-w-4xl"
        >
          <MaskedLine delay={0.35}>{LINES[0]}</MaskedLine>
          <MaskedLine delay={0.5}>{LINES[1]}</MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="italic text-bronze">{LINES[2]}</span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          className="mt-8 max-w-xl"
        >
          <p className="text-base md:text-lg font-light leading-relaxed text-taupe">
            Du hast längst verstanden, dass du loslassen solltest. Aber etwas in dir
            hält immer noch fest.
          </p>
          <p className="mt-4 text-base md:text-lg font-light leading-relaxed text-taupe">
            Vielleicht ist es ein Mensch. Eine Verletzung. Eine Enttäuschung. Worte,
            die gefallen sind – und die du trotzdem nicht vergessen kannst.
          </p>
          <p className="mt-4 text-base md:text-lg font-light leading-relaxed text-taupe">
            Du funktionierst. Du machst weiter. Und trotzdem gibt es diese Momente,
            in denen alles wieder da ist.
          </p>
          <p data-testid="hero-highlight-question" className="mt-8 font-serif italic text-xl md:text-2xl leading-snug text-espresso tracking-tight">
            «Aber wie lasse ich etwas los,<br />das mich einfach nicht loslässt?»
          </p>
          <p className="mt-8 text-base md:text-lg font-light leading-relaxed text-taupe">
            Genau darum geht es an diesem Abend. Du musst nicht noch mehr darüber
            nachdenken. Ich begleite dich 60 Minuten durch eine geführte
            Hypnomeditation – damit dein Kopf leiser werden, dein Körper zur Ruhe
            kommen und du dich deinem inneren Erleben zuwenden kannst.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-7"
        >
          <button
            data-testid="hero-booking-button"
            onClick={onBook}
            className="group w-fit rounded-full bg-espresso px-9 py-4 text-sm md:text-base font-medium tracking-wide text-cream transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            JA, ICH MÖCHTE LOSLASSEN
          </button>
          <div data-testid="hero-event-facts" className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-taupe">
            <span className="flex items-center gap-2"><Calendar size={15} className="text-gold" /> Do, 03.09.2026</span>
            <span className="flex items-center gap-2"><Clock size={15} className="text-gold" /> 20:00 – 21:00 Uhr</span>
            <span className="flex items-center gap-2"><Video size={15} className="text-gold" /> Live via Zoom</span>
            <span className="font-medium text-espresso">CHF 21.–</span>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative z-10 flex justify-center pb-8"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="text-bronze/60"
        >
          <ArrowDown size={20} strokeWidth={1.5} />
        </motion.span>
      </motion.div>
    </header>
  );
}
